import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarClock,
  ClipboardList,
  Gem,
  MapPin,
  Server,
  SquarePen,
  StickyNote,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import { queryClient } from "App";
import { linkedTabSections, useLinkedTables } from "components/linkedTables";
import PageContainer, { SECTION_TABLE_STYLES } from "components/PageContainer";
import _RevenueOpportunitiesTable from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableV3";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import withRoleCheck from "components/hocs/withRoleCheck";
import AuditLog from "components/views/AuditLog";
import NotesView from "components/views/NotesView";
import { noTag } from "constants/Tag";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import useShowDisabledTags from "hooks/useShowDisabledTags";
import CaseFormModal from "routes/case/CaseFormModal";
import CaseInfoPanel from "routes/case/CaseInfoPanel";
import CaseEventsTableV3 from "routes/case_event/CaseEventsTableV3";
import EntityContactsTable from "routes/contact/components/contacts_table/ContactsTableV3";
import MachinesTableV3 from "routes/machine/MachinesTableV3";
import ActivityService from "services/Activity";
import AuthService from "services/Auth";
import CaseService from "services/Case";
import EntityService from "services/Entity";

const RevenueOpportunitiesTable = withRoleCheck(
  _RevenueOpportunitiesTable,
  [ACL_RELATIONSHIPS.revenueOpportunity.readTable],
  { showMsg: true }
);

const initialCase = {
  id: null,
  name: "",
  case_id: null,
  entity_id: null,
  tags: [noTag],
  custom_fields: [],
  case_investigators: [],
  addresses: [],
};

const fetchCase = async (caseId, showDisabledTags) => {
  const model = await CaseService.get(caseId, {
    show_disabled_tags: showDisabledTags,
  });
  if (model.tags.length === 0) {
    model.tags = [noTag];
  }
  return model;
};

const fetchEntity = async (entityId, showDisabledTags) => {
  const entity = await EntityService.getEntity(entityId, {
    show_disabled_tags: showDisabledTags,
  });
  if (entity.tags.length === 0) {
    entity.tags = [noTag];
  }
  return entity;
};

const CaseDetail = () => {
  const { id: modelId } = useParams();
  const navigate = useNavigate();
  const [auditLogRefreshFlag, setAuditLogRefreshFlag] = useState(1);
  const showDisabledTags = useShowDisabledTags();
  const showDisabledTagsParam = showDisabledTags ? "true" : "false";

  const { activeTab, handleTabChange } = useAppTabs({
    basePath: `/cases/${modelId}`,
    defaultTab: "activityLog",
  });

  const canViewMap = useAuthorized([ACL_RELATIONSHIPS.machine.read]);

  const { data: caseData, isFetched: isCaseFetched } = useQuery({
    queryKey: ["case", modelId, showDisabledTagsParam],
    queryFn: () => fetchCase(modelId, showDisabledTagsParam),
    initialData: initialCase,
    enabled: AuthService.isLoggedIn() && !!modelId,
  });

  const { data: entity, isFetched: isEntityFetched } = useQuery({
    queryKey: ["entity", caseData.entity_id, showDisabledTagsParam],
    queryFn: () => fetchEntity(caseData.entity_id, showDisabledTagsParam),
    enabled: !!caseData.entity_id,
  });

  const { data: hasNotes } = useQuery({
    queryKey: ["case", modelId, "hasNotes"],
    queryFn: () => ActivityService.hasNotes(modelId, "Case"),
    enabled: !!modelId,
  });

  const linkedTabs = useLinkedTables("Case", modelId);

  usePageTitle(caseData.name || "Pleteo");

  const invalidateCaseDetailQueries = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["case", modelId] });
    await queryClient.invalidateQueries({
      queryKey: ["entity", caseData.entity_id],
    });
    setAuditLogRefreshFlag(prev => prev + 1);
  }, [modelId, caseData.entity_id]);

  const sections = useMemo(() => {
    const items = [
      {
        key: "activityLog",
        label: "Activity Log",
        icon: ClipboardList,
        content: (
          <AuditLog
            modelId={modelId}
            modelType="Case"
            updateFlag={auditLogRefreshFlag}
          />
        ),
      },
      {
        key: "machines",
        label: "Machines",
        icon: Server,
        content: (
          <PageContainer styles={SECTION_TABLE_STYLES}>
            <MachinesTableV3
              caseRouteId={caseData.id}
              caseId={caseData.case_id}
              productLineId={caseData.product_line_id}
            />
          </PageContainer>
        ),
      },
      {
        key: "events",
        label: "Events",
        icon: CalendarClock,
        content: (
          <PageContainer styles={SECTION_TABLE_STYLES}>
            <CaseEventsTableV3
              caseId={caseData.case_id}
              productLineId={caseData.product_line_id}
            />
          </PageContainer>
        ),
      },
      {
        key: "contacts",
        label: "Contacts",
        icon: Users,
        hidden: !caseData.entity_id,
        content: (
          <PageContainer styles={SECTION_TABLE_STYLES}>
            <EntityContactsTable entityId={caseData.entity_id} />
          </PageContainer>
        ),
      },
      {
        key: "notes",
        label: "Notes",
        icon: StickyNote,
        badge: hasNotes && activeTab !== "notes" && (
          <span
            aria-label="Has notes"
            className="tw:size-2 tw:rounded-full tw:bg-sidebar-primary"
          />
        ),
        content: (
          <NotesView
            modelType="Case"
            modelId={modelId}
            handleTabChange={handleTabChange}
          />
        ),
      },
      {
        key: "revenueOpportunities",
        label: "Revenue Opportunities",
        icon: Gem,
        content: (
          <PageContainer styles={SECTION_TABLE_STYLES}>
            <RevenueOpportunitiesTable
              caseId={caseData.id}
              entityId={caseData.entity_id}
            />
          </PageContainer>
        ),
      },
    ];
    items.push(...linkedTabSections(linkedTabs, modelId));
    return items;
  }, [
    modelId,
    caseData,
    hasNotes,
    activeTab,
    handleTabChange,
    auditLogRefreshFlag,
    linkedTabs,
  ]);

  if (!caseData.id) {
    return null;
  }

  return (
    <SidebarDetailLayout
      sections={sections}
      basePath={`/cases/${modelId}`}
      defaultSection="activityLog"
      header="Case"
      breadcrumb={
        <DetailBreadcrumb
          parentTo="/cases"
          parentLabel="All Cases"
          current={caseData.name}
        />
      }
      title={caseData.name}
      titleActions={
        <>
          {canViewMap && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/cases/${modelId}/map`)}
            >
              <MapPin data-icon="inline-start" />
              Map
            </Button>
          )}
          <CaseFormModal
            caseId={modelId}
            fetchData={invalidateCaseDetailQueries}
            trigger={
              <Button variant="outline" size="sm">
                <SquarePen data-icon="inline-start" />
                Edit
              </Button>
            }
          />
        </>
      }
      info={
        <CaseInfoPanel
          caseData={caseData}
          entity={entity}
          showDisabledTags={showDisabledTags}
          onUpdate={invalidateCaseDetailQueries}
          isLoading={!isCaseFetched}
          isEntityLoading={!!caseData.entity_id && !isEntityFetched}
        />
      }
      infoLabel="Case Details"
    />
  );
};

export default CaseDetail;
